import type { Author, AuthorStats, Manuscript } from './types';
import { mulberry32 } from './bookCatalog';
import { NORSKE_ETTERNAVN, NORSKE_FORNAVN } from './names';
import type { Tier } from '@/state/GameState';

type TierRange = { snitt: number; min: number; max: number };

const TIER_STATS: Record<Tier, TierRange> = {
  D: { snitt: 35, min: 15, max: 60 },
  C: { snitt: 50, min: 25, max: 75 },
  B: { snitt: 65, min: 40, max: 90 },
  A: { snitt: 75, min: 50, max: 95 },
  S: { snitt: 85, min: 65, max: 100 },
};

const STAT_KEYS: ReadonlyArray<keyof AuthorStats> = [
  'dialog',
  'plot',
  'karakter',
  'spraak',
  'voice',
  'autentisitet',
  'disiplin',
];

export function tierFraStats(stats: AuthorStats): Tier {
  let sum = 0;
  for (const key of STAT_KEYS) {
    sum += stats[key];
  }
  const snitt = sum / STAT_KEYS.length;
  if (snitt < 50) return 'D';
  if (snitt < 65) return 'C';
  if (snitt < 78) return 'B';
  if (snitt < 88) return 'A';
  return 'S';
}

export function genererAuthor(manus: Manuscript, seed?: number): Author {
  const rng = seed === undefined ? Math.random : mulberry32(seed);
  const range = TIER_STATS[manus.tier];

  const stats = {} as AuthorStats;
  for (const key of STAT_KEYS) {
    stats[key] = lagStat(rng, range);
  }

  const erUkjent = !manus.author || manus.author.trim().toLowerCase() === 'ukjent forfatter';
  const navn = erUkjent ? lagPseudonym(rng) : manus.author;

  return {
    id: lagId(rng),
    navn,
    pseudonym: erUkjent,
    primaerSjanger: manus.genre,
    stats,
    lojalitet: 50,
    stress: 30,
    ego: 40,
    drikkenivaa: 20,
    karriere: [],
    lojalitetHistorikk: [],
  };
}

function lagStat(rng: () => number, range: TierRange): number {
  const spread = (range.max - range.min) / 2;
  const jitter = (rng() - 0.5) * spread * 2;
  const raw = Math.round(range.snitt + jitter * 0.6);
  return Math.max(range.min, Math.min(range.max, raw));
}

function lagPseudonym(rng: () => number): string {
  const f = NORSKE_FORNAVN[Math.floor(rng() * NORSKE_FORNAVN.length)];
  const e = NORSKE_ETTERNAVN[Math.floor(rng() * NORSKE_ETTERNAVN.length)];
  return `${f} ${e}`;
}

function lagId(rng: () => number): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const hex = Math.floor(rng() * 0xffffffff).toString(16).padStart(8, '0');
  const tail = Math.floor(rng() * 0xffffffff).toString(16).padStart(8, '0');
  return `auth-${hex}${tail}`;
}
